import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';

import { googleMapsLink } from '../../../utils/shippingMethods';

// Centro de Lima: vista inicial del mapa mientras no haya un punto marcado.
const LIMA_CENTER = [-12.0464, -77.0428];

// Pin dibujado con CSS (divIcon) en vez del icono por defecto de Leaflet, cuyas imagenes no
// se resuelven bien al empaquetar con webpack.
const pinIcon = L.divIcon({
  className: '',
  html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:#d32f2f;
    transform:rotate(-45deg);border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

const roundCoordinate = (value) => Math.round(value * 1e7) / 1e7;

// Localizador GPS del envio a domicilio: el cliente marca su punto exacto (con "Usar mi
// ubicacion", tocando el mapa o arrastrando el pin), aparte de la direccion escrita.
// `value` es { latitude, longitude } o null.
export default function LocationPicker({ value, onChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState('');

  onChangeRef.current = onChange;

  useEffect(() => {
    const map = L.map(containerRef.current).setView(value ? [value.latitude, value.longitude] : LIMA_CENTER, value ? 17 : 12);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);
    map.on('click', (event) => {
      onChangeRef.current({ latitude: roundCoordinate(event.latlng.lat), longitude: roundCoordinate(event.latlng.lng) });
    });
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // El mapa se crea una sola vez; los cambios de `value` los sincroniza el efecto de abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    if (!value) {
      markerRef.current?.remove();
      markerRef.current = null;
      return;
    }

    const position = [value.latitude, value.longitude];
    if (markerRef.current) {
      markerRef.current.setLatLng(position);
    } else {
      markerRef.current = L.marker(position, { icon: pinIcon, draggable: true }).addTo(map);
      markerRef.current.on('dragend', (event) => {
        const { lat, lng } = event.target.getLatLng();
        onChangeRef.current({ latitude: roundCoordinate(lat), longitude: roundCoordinate(lng) });
      });
    }
    if (!map.getBounds().contains(position)) {
      map.setView(position, 17);
    }
  }, [value]);

  const handleLocate = () => {
    if (!navigator.geolocation) {
      setLocateError('Tu navegador no permite obtener la ubicación. Marca tu punto tocando el mapa.');
      return;
    }
    setLocating(true);
    setLocateError('');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLocating(false);
        const point = { latitude: roundCoordinate(coords.latitude), longitude: roundCoordinate(coords.longitude) };
        onChange(point);
        mapRef.current?.setView([point.latitude, point.longitude], 17);
      },
      () => {
        setLocating(false);
        setLocateError('No pudimos obtener tu ubicación. Revisa el permiso de ubicación o marca tu punto tocando el mapa.');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2">Ubicación en el mapa</Typography>
        <Button size="small" variant="outlined" startIcon={<MyLocationIcon />} onClick={handleLocate} disabled={locating}>
          {locating ? 'Buscando...' : 'Usar mi ubicación'}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Toca el mapa o arrastra el pin hasta el punto exacto de entrega, para que el repartidor llegue sin problemas.
      </Typography>
      <Box
        ref={containerRef}
        sx={{ height: 280, borderRadius: 1, overflow: 'hidden', border: 1, borderColor: 'divider', zIndex: 0 }}
      />
      {locateError && <Alert severity="warning">{locateError}</Alert>}
      {value && (
        <Typography variant="caption" color="text.secondary">
          Punto marcado: {value.latitude}, {value.longitude} ·{' '}
          <Link href={googleMapsLink(value.latitude, value.longitude)} target="_blank" rel="noopener noreferrer">
            Ver en Google Maps
          </Link>
        </Typography>
      )}
    </Stack>
  );
}
